// INC2-CONTACT: "Grant access" as a modal (quick actions are modals; integrations §2.6, §2.10).
// Who (a person or DE, a seat, a team), how far (Read · Act · Manage), which families, and
// limits (acts a day; mail recipients' domains). A person's own connection goes to a named DE
// only, never at Manage, and Act names its families (the server holds the same rules).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import type { Connection } from "../subscriptions/queries";
import {
  FAMILIES,
  FAMILY_LABEL,
  type Family,
  type GranteeKind,
  type GrantLevel,
  grantKeys,
  LEVEL_INFO,
  LEVEL_LABEL,
  LEVELS,
  type useGrantees,
} from "./grants";

type Names = ReturnType<typeof useGrantees>;

export function GrantDialog({
  api,
  co,
  connection,
  names,
  open,
  onOpenChange,
}: {
  api: Api;
  co: string;
  connection: Connection;
  names: Names;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const ids = { who: useId(), kind: useId(), daily: useId(), domains: useId() };
  const personal = connection.scope === "person";
  const [kind, setKind] = useState<Exclude<GranteeKind, "crew">>("membership");
  const [who, setWho] = useState("");
  const [level, setLevel] = useState<GrantLevel>("use_read");
  const [families, setFamilies] = useState<Family[]>([]);
  const [daily, setDaily] = useState("");
  const [domains, setDomains] = useState("");
  const reset = () => {
    setKind("membership");
    setWho("");
    setLevel("use_read");
    setFamilies([]);
    setDaily("");
    setDomains("");
  };
  const options =
    kind === "seat"
      ? names.seats.map((s) => ({ id: s.id, label: s.title }))
      : kind === "team"
        ? names.teams.map((t) => ({ id: t.id, label: t.name }))
        : [
            ...names.des.map((d) => ({ id: d.membership_id, label: `DE · ${d.display_name}` })),
            ...(personal
              ? []
              : names.people
                  .filter((p) => p.kind === "person")
                  .map((p) => ({ id: p.id, label: p.display_name }))),
          ];
  const dailyN = daily.trim() ? Number(daily) : null;
  const domainList = domains
    .split(/[\s,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  const needsFamilies = personal && level === "use_act" && families.length === 0;
  const badDaily = dailyN !== null && (!Number.isInteger(dailyN) || dailyN < 1 || dailyN > 10_000);
  const ready = who !== "" && !needsFamilies && !badDaily;
  const grant = useMutation({
    mutationFn: () =>
      api.client.connectionGrants.create({
        params: { co, id: connection.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          grantee_kind: kind,
          grantee_id: who,
          level,
          ...(families.length ? { families } : {}),
          ...(dailyN !== null || domainList.length
            ? {
                limits: {
                  ...(dailyN !== null ? { daily_acts: dailyN } : {}),
                  ...(domainList.length ? { recipient_domains: domainList } : {}),
                },
              }
            : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: grantKeys.all(co) });
      toast.add({ title: `Access granted on ${connection.label}`, type: "success" });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.add({ title: problemText(e, "Not granted."), type: "error" }),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-slot="grant-dialog">
        <DialogHeader>
          <DialogTitle>Grant access</DialogTitle>
          <DialogDescription>{connection.label}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          aria-label="Grant access"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) grant.mutate();
          }}
        >
          {personal ? null : (
            <Field>
              <FieldLabel htmlFor={ids.kind}>Grant to</FieldLabel>
              <NativeSelect
                id={ids.kind}
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as typeof kind);
                  setWho("");
                }}
              >
                <NativeSelectOption value="membership">A person or DE</NativeSelectOption>
                <NativeSelectOption value="seat">A seat</NativeSelectOption>
                <NativeSelectOption value="team">A team</NativeSelectOption>
              </NativeSelect>
            </Field>
          )}
          <Field>
            <span className="flex items-center gap-1">
              <FieldLabel htmlFor={ids.who}>{personal ? "DE" : "Who"}</FieldLabel>
              {personal ? (
                <InfoTip label="About granting your connection">
                  Your own connection goes only to a named DE. Your personal agent's access is set
                  in My agent.
                </InfoTip>
              ) : null}
            </span>
            <NativeSelect
              id={ids.who}
              value={who}
              disabled={names.error != null}
              onChange={(e) => setWho(e.target.value)}
            >
              <NativeSelectOption value="">
                {names.error ? "Could not load" : names.loading ? "Loading…" : "Choose…"}
              </NativeSelectOption>
              {options.map((o) => (
                <NativeSelectOption key={o.id} value={o.id}>
                  {o.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">Level</legend>
            <div className="flex flex-wrap gap-4">
              {LEVELS.filter((l) => !(personal && l === "manage")).map((l) => (
                <span key={l} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    id={`${ids.who}-${l}`}
                    name={`${ids.who}-level`}
                    value={l}
                    checked={level === l}
                    onChange={() => setLevel(l)}
                  />
                  <Label htmlFor={`${ids.who}-${l}`} className="font-normal">
                    {LEVEL_LABEL[l]}
                  </Label>
                  <InfoTip label={`About ${LEVEL_LABEL[l]}`}>{LEVEL_INFO[l]}</InfoTip>
                </span>
              ))}
            </div>
          </fieldset>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              Families
              <InfoTip label="About families">
                The tool families this grant opens. None ticked: every family the connection serves
                {personal ? " (Act needs at least one)" : ""}.
              </InfoTip>
            </legend>
            <div className="flex flex-wrap gap-4">
              {FAMILIES.map((f) => (
                <span key={f} className="flex items-center gap-1.5">
                  <Checkbox
                    id={`${ids.who}-f-${f}`}
                    checked={families.includes(f)}
                    onCheckedChange={(v) =>
                      setFamilies((x) => (v === true ? [...x, f] : x.filter((y) => y !== f)))
                    }
                  />
                  <Label htmlFor={`${ids.who}-f-${f}`} className="font-normal">
                    {FAMILY_LABEL[f]}
                  </Label>
                </span>
              ))}
            </div>
            {needsFamilies ? (
              <p className="text-xs text-muted-foreground">Name the families a DE may act in.</p>
            ) : null}
          </fieldset>
          {level === "use_read" ? null : (
            <div className="grid gap-4 md:grid-cols-2">
              <Field>
                <span className="flex items-center gap-1">
                  <FieldLabel htmlFor={ids.daily}>Acts a day</FieldLabel>
                  <InfoTip label="About acts a day">
                    At most this many acts go out through it per UTC day. Empty: no cap.
                  </InfoTip>
                </span>
                <Input
                  id={ids.daily}
                  inputMode="numeric"
                  value={daily}
                  aria-invalid={badDaily || undefined}
                  onChange={(e) => setDaily(e.target.value)}
                />
              </Field>
              <Field>
                <span className="flex items-center gap-1">
                  <FieldLabel htmlFor={ids.domains}>Mail domains</FieldLabel>
                  <InfoTip label="About mail domains">
                    Mail goes only to these domains (comma separated). Empty: any.
                  </InfoTip>
                </span>
                <Input
                  id={ids.domains}
                  className="font-mono"
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" size="sm" disabled={!ready || grant.isPending}>
              Grant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
